import * as Sentry from "@sentry/capacitor";
import {
  BREAKFAST_ALREADY_SET_UP_REFUSAL,
  PROPERTY_PREPARING_REFUSAL,
} from "@vidimo/shared";
import {
  formatUploadOpId,
  type PowerSyncUploadResult,
} from "@vidimo/shared/contracts";
import { toast } from "sonner";
import type { InterfaceMessages } from "@/i18n/catalogs";
import { interfaceTranslator } from "@/i18n/translate";
import { outcomeHaptic } from "@/lib/haptics";
import { preparingMessage } from "@/lib/operational-state";
import { isFirstReport } from "@/lib/report-distinct";

/**
 * Identity of an op we uploaded — what we know about a discarded write without
 * its row payload. Diagnostics stay PII-free (the rows carry guest data).
 */
export interface UploadOpIdentity {
  id: string;
  op: string;
  type: string;
}

const DISCARD_TOAST_ID = "powersync-upload-discarded";

/**
 * Refusals the server makes on purpose, each with what the host is told. Each
 * has its own toast, so the discard of a later edit cannot replace the reason.
 */
const REASONED_REFUSALS = new Map<
  string | undefined,
  { id: string; message: () => string }
>([
  [
    PROPERTY_PREPARING_REFUSAL,
    { id: "powersync-upload-preparing", message: preparingMessage },
  ],
  [
    BREAKFAST_ALREADY_SET_UP_REFUSAL,
    {
      id: "powersync-upload-breakfast-set-up",
      message: () =>
        interfaceTranslator("shell.powersyncUploadFeedback")(
          "breakfastAlreadySetUp"
        ),
    },
  ],
]);

type EntityLabelKey =
  keyof InterfaceMessages["shell"]["powersyncUploadFeedback"]["labels"];

/**
 * User-facing singular noun for each client-written PowerSync table, so the
 * discard toast can name what was reverted ("changes to your reservation")
 * instead of a bare "changes". A table with no entry (or a mix of entries)
 * falls back to the generic wording — never a raw snake_case table name.
 */
const ENTITY_LABEL_KEYS: Record<string, EntityLabelKey> = {
  ai_chat_messages: "message",
  ai_chats: "conversation",
  guest_chats: "conversation",
  guest_message_reactions: "reaction",
  guest_messages: "message",
  guests: "guest",
  properties: "property",
  property_breakfast: "breakfast",
  rate_calendar: "pricing",
  rate_plans: "pricing",
  reservation_charge: "charge",
  reservation_payment: "payment",
  reservation_units: "reservation",
  reservations: "reservation",
  staff_availability_window: "availability",
  staff_chat_participants: "conversation",
  staff_chats: "conversation",
  staff_message_reactions: "reaction",
  staff_messages: "message",
  staff_time_off: "timeOff",
  suggestions: "suggestion",
  tasks: "task",
  units: "unit",
};

/**
 * The single entity label to name in the toast, or null to stay generic. Only
 * names it when every discarded op maps to the same known label — a mix of
 * entities (or an unknown table) reads better as a plain "Some changes".
 */
function discardedEntityLabel(tables: string[]): EntityLabelKey | null {
  const labels = new Set(tables.map((table) => ENTITY_LABEL_KEYS[table]));
  const [only] = labels;
  return labels.size === 1 && only ? only : null;
}

/**
 * Surface upload ops the server rejected. The route acks a permanently-rejected
 * op with a 2xx + an `error` marker so the CRUD queue keeps moving (a poison op
 * must not wedge later uploads, which PowerSync processes in order); PowerSync
 * then clears the op, dropping the optimistic local edit, which snaps back to
 * the server value on the next read.
 *
 * That discard must not be silent (a wire-shape drift can otherwise drop every
 * edit to a table for months unnoticed), so each rejected op gets:
 *  - one deduplicated toast so the user learns an edit didn't stick, and
 *  - a Sentry event (table + op kind + id + the server's error, never the row
 *    payload) so the drift surfaces in hours.
 */
export function reportDiscardedUploadOps(
  failed: PowerSyncUploadResult[],
  uploaded: UploadOpIdentity[]
): void {
  if (failed.length === 0) {
    return;
  }

  const identityByOpId = new Map(
    uploaded.map((op) => [formatUploadOpId(op), op])
  );

  const discardedTables: string[] = [];
  for (const { opId, error } of failed) {
    const identity = identityByOpId.get(opId);
    const table = identity?.type ?? "unknown";
    const opKind = identity?.op ?? "unknown";
    const serverError = error ?? "unknown error";
    discardedTables.push(table);

    console.error(
      `[powersync] upload op rejected and discarded: ${opId} — ${serverError}`
    );

    Sentry.captureException(
      new Error(`PowerSync upload rejected (${table}): ${serverError}`),
      {
        // Op identity + the server's message only — never the row payload, which
        // carries guest PII (see the scrubbers wired in sentry.ts).
        extra: { op: opKind, rowId: identity?.id, serverError, table },
        // Group by table + op kind so recurring drift collapses into one issue
        // rather than one-per-row (the synthetic Error shares a single stack).
        fingerprint: ["powersync-upload-discarded", table, opKind],
        tags: { area: "powersync-upload", op: opKind, table },
      }
    );
  }

  // Refused on purpose: say why instead of "some changes were reverted", which
  // reads like a fault.
  const reasons = new Set(
    failed.map(({ error }) => REASONED_REFUSALS.get(error))
  );
  const [reasoned] = reasons.size === 1 ? reasons : [];
  const label = discardedEntityLabel(discardedTables);
  // The host did not ask for this and is not waiting on it: an edit they made
  // earlier has just been thrown away. The toast is the only channel, it is
  // transient, and it lands whenever the CRUD queue happens to flush, which
  // may be long after the edit and on a different screen. The buzz is what
  // makes them look. `uploadData` walks the queue in a loop, so this leans on
  // the shared burst window in `haptics.ts` to stay one buzz per flush.
  outcomeHaptic("error");
  const t = interfaceTranslator("shell.powersyncUploadFeedback");
  let message = t("discarded");
  let id = DISCARD_TOAST_ID;
  if (reasoned) {
    message = reasoned.message();
    ({ id } = reasoned);
  } else if (label) {
    message = t("discardedEntity", { label: t(`labels.${label}`) });
  }
  toast.error(message, { id });
}

const reportedStuckUploads = new Set<string>();

/** The failures the connector words itself, up to the response body. */
const CONNECTOR_FAILURE = /^\[powersync\] (?:upload|token) failed [^\s:]+/;

/**
 * Surface an upload queue that stopped moving. PowerSync retries a failing
 * upload forever and the host's edits wait on the phone meanwhile, so each
 * distinct error behind a stuck queue reaches Sentry once; the banner tells
 * the host. Only the connector's own wording goes out, past which a response
 * body can echo a row; any other error goes as its name. No error means the
 * upload itself never settled.
 */
export function reportStuckUpload(uploadError: Error | undefined): void {
  const serverError = uploadError
    ? (CONNECTOR_FAILURE.exec(uploadError.message)?.[0] ?? uploadError.name)
    : "no upload error";
  if (isFirstReport(reportedStuckUploads, serverError)) {
    Sentry.captureException(
      new Error(`PowerSync upload stuck: ${serverError}`),
      {
        fingerprint: ["powersync-upload-stuck", serverError],
        tags: { area: "powersync-upload" },
      }
    );
  }
}
